import React from "react";
import { useSearchParams } from "react-router-dom";
import { FileText, Search, RefreshCw } from "lucide-react";
import { breadcrumbSchema } from "@/components/Seo";
import PageSeo from "@/components/PageSeo";
import PageHeader from "@/components/public/PageHeader";
import TrackWidget from "@/components/public/TrackWidget";
import { Reveal } from "@/components/public/Primitives";
import { useLanguage } from "@/context/LanguageContext";

const HOW_IT_WORKS = [
  { icon: FileText, title: "Submit your issue", desc: "Report a Hoodi Ward 29 issue through the Submit Issue form and receive a unique reference ID (e.g. HW29-A1B2C3)." },
  { icon: Search, title: "Track anytime", desc: "Enter your reference ID here to see the live status of your issue at any time." },
  { icon: RefreshCw, title: "Honest follow-up", desc: "The public service team reviews, follows up with authorities, and updates the status until it is resolved." },
];

export default function TrackIssue() {
  const [params] = useSearchParams();
  const initialRef = params.get("ref") || "";
  const { tr } = useLanguage();

  return (
    <div data-testid="track-issue-page">
      <PageSeo
        slug="track-issue"
        title="Track Your Hoodi Ward 29 Issue | Girish Kumar Naidu"
        description="Track the live status of your submitted Hoodi Ward 29 issue using your reference ID — from received and reviewed to in progress and resolved."
        keywords="track Hoodi Ward 29 issue, issue status, Hoodi civic issue tracker, Girish Kumar Naidu helpdesk"
        schemas={[breadcrumbSchema([{ name: "Home", url: window.location.origin }, { name: "Track Your Issue" }])]}
      />
      <PageHeader
        overline={tr("page.track.overline")}
        title={tr("page.track.title")}
        subtitle={tr("page.track.subtitle")}
        crumbs={[{ name: "Track Your Issue" }]}
      />

      <section className="bg-navy py-16 md:py-24 relative grain overflow-hidden">
        <div className="max-w-4xl mx-auto px-5 md:px-8 relative">
          <TrackWidget initialRef={initialRef} />
        </div>
      </section>

      <section className="bg-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="grid md:grid-cols-3 gap-6">
            {HOW_IT_WORKS.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.08}>
                <div className="bg-white rounded-2xl p-8 border border-gray-200 card-lift h-full">
                  <div className="flex items-start justify-between mb-5">
                    <div className="h-12 w-12 rounded-xl bg-navy flex items-center justify-center">
                      <s.icon className="h-6 w-6 text-gold" />
                    </div>
                    <span className="font-mono text-xs text-gray-300 font-bold">{String(i + 1).padStart(2, "0")}</span>
                  </div>
                  <h3 className="font-display text-xl font-bold text-navy mb-2">{s.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
